import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useRetranslateErrors } from '@/lib/forms/useRetranslateErrors';
import { authService } from '@services/auth.service';
import {
  createForgotPasswordSchema,
  type ForgotPasswordFormValues,
} from '../schemas/forgotPassword.schema';

/**
 * Forgot-password controller: RHF + zod, calling `authService` directly (no
 * Redux, matching the legacy flow). On success it surfaces a "check your email"
 * panel; failures surface inline via `error` (toast wiring lands in a separate PR).
 */
export function useForgotPasswordForm() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isSuccess, setIsSuccess] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  // The key, not the resolved text: nothing re-runs the submit on a language toggle.
  const [errorKey, setErrorKey] = useState<string | null>(null);

  const schema = useMemo(() => createForgotPasswordSchema(t), [t]);
  const form = useForm<ForgotPasswordFormValues>({
    resolver: zodResolver(schema),
    defaultValues: { tenantCode: '', email: '' },
  });

  useRetranslateErrors(form);

  const onSubmit = form.handleSubmit(async (values) => {
    setIsSubmitting(true);
    setErrorKey(null);
    try {
      const result = await authService.requestPasswordReset({
        email: values.email,
        tenantCode: values.tenantCode,
      });
      if (result.success) {
        setSubmittedEmail(values.email);
        setIsSuccess(true);
      } else {
        // Localized copy rather than the raw axios message (e.g. "…status code 422").
        setErrorKey('auth.sendResetLinkError');
      }
    } catch {
      setErrorKey('auth.sendResetLinkError');
    } finally {
      setIsSubmitting(false);
    }
  });

  return {
    form,
    onSubmit,
    isSubmitting,
    isSuccess,
    submittedEmail,
    error: errorKey ? t(errorKey) : null,
    goToLogin: () => navigate('/login'),
  };
}
